"use client";

import React, { useState } from "react";

// Preparation Section Component
function PreparationSection() {
  const [activeTab, setActiveTab] = useState('before');

  const preparationData = {
    before: {
      title: "Before the Appraiser Arrives",
      subtitle: "Set the stage for success with these essential preparations",
      color: "from-blue-600 to-blue-700",
      icon: "M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
      items: [
        {
          icon: "📋",
          title: "Gather Documentation",
          description: "Compile receipts, permits, and photos of recent improvements"
        },
        {
          icon: "✨",
          title: "Deep Clean Everything",
          description: "Professional cleaning creates the best first impression"
        },
        {
          icon: "🔧",
          title: "Complete Minor Repairs",
          description: "Fix small issues that could signal bigger problems"
        },
        {
          icon: "🌿",
          title: "Enhance Curb Appeal",
          description: "Mow, trim, and ensure exterior looks well-maintained"
        }
      ]
    },
    during: {
      title: "During the Appraisal Visit",
      subtitle: "Best practices while the appraiser is on-site",
      color: "from-green-600 to-green-700",
      icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      items: [
        {
          icon: "👋",
          title: "Be Available but Respectful",
          description: "Answer questions without being overbearing"
        },
        {
          icon: "🔓",
          title: "Provide Full Access",
          description: "Unlock all areas including storage and utility rooms"
        },
        {
          icon: "🐕",
          title: "Minimize Distractions",
          description: "Keep pets and children in a separate area"
        },
        {
          icon: "💡",
          title: "Highlight Improvements",
          description: "Mention upgrades if asked, but don't oversell"
        }
      ]
    },
    after: {
      title: "After the Appraisal",
      subtitle: "What to expect and next steps",
      color: "from-purple-600 to-purple-700",
      icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
      items: [
        {
          icon: "⏱️",
          title: "Wait for Report",
          description: "Reports typically delivered within 5-10 business days"
        },
        {
          icon: "📊",
          title: "Review Thoroughly",
          description: "Check all property details and comparable sales"
        },
        {
          icon: "❓",
          title: "Ask Questions",
          description: "Contact us if anything seems unclear or incorrect"
        },
        {
          icon: "📄",
          title: "Use for Your Purpose",
          description: "Report is ready for legal, tax, or financial needs"
        }
      ]
    }
  };

  const currentData = preparationData[activeTab];

  return (
    <section className="mb-12 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 p-8 rounded-2xl">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
          How to Prepare for Your Home Appraisal
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Simple steps to <strong>maximize your property value</strong> during the appraisal process
        </p>
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Navigation Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white dark:bg-gray-800 p-1 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            {[
              { key: 'before', label: 'Before Visit' },
              { key: 'during', label: 'During Visit' },
              { key: 'after', label: 'After Visit' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className={`bg-gradient-to-r ${currentData.color} text-white p-6`}>
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={currentData.icon} />
              </svg>
              <div>
                <h3 className="text-2xl font-bold">{currentData.title}</h3>
                <p className="opacity-90">{currentData.subtitle}</p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-6">
              {currentData.items.map((item, index) => (
                <div key={index} className="relative overflow-hidden p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/30 dark:to-gray-800/30 rounded-xl hover:shadow-md transition-all duration-300 border border-gray-200/50 dark:border-gray-600/30">
                  {/* Background Icon */}
                  <div className="absolute top-2 right-2 text-6xl opacity-10 dark:opacity-5 grayscale pointer-events-none select-none">
                    {item.icon}
                  </div>
                  
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent dark:from-white/5 dark:to-transparent pointer-events-none"></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-lg tracking-tight">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        
      </div>
    </section>
  );
}

export default PreparationSection;